"use client";

import AppButton from "@/components/app-button";
import { EPermission } from "@/constants/permission.enum";
import type { IUserRole } from "@/features/privileges/roles/types";
import { withRBAC } from "@/lib/action-utils";
import { PermissionDeniedError } from "@/lib/errors";
import { http } from "@/lib/http";
import { getBaseResourceUrl } from "@/lib/utils";
import clsx from "clsx";
import { Download, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx-js-style";

interface ExportButtonProps {
  searchCondition?: string;
}

export function ExportButton({ searchCondition }: ExportButtonProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = async () => {
    try {
      setIsLoading(true);

      const protectedExport = withRBAC({
        requiredPermissions: [EPermission.Export],
        resourcePath: getBaseResourceUrl(pathname),
      })(async () => {
        const params = new URLSearchParams({
          searchCondition: searchCondition || "",
        });
        const response = await http.get<IUserRole[]>(
          `v1/payroll/user-roles/export?${params.toString()}`
        );
        const data = response.data;

        if (!data || data.length === 0) {
          toast.error(t("notification.no-data-to-export"));
          return false;
        }

        const worksheet = XLSX.utils.json_to_sheet(
          data.map((item) => ({
            [t("feature.user-role.user")]: item.userName ?? "",
            [t("feature.user-role.role")]: item.role?.name ?? "",
          }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "UserRoles");
        XLSX.writeFile(workbook, `phan_quyen_nguoi_dung_${Date.now()}.xlsx`);
        return true;
      });

      const exported = await protectedExport();
      if (exported) {
        toast.success(t("notification.exported-successfully"));
      }
    } catch (error) {
      if (error instanceof PermissionDeniedError) {
        toast.error(error.message);
      } else {
        toast.error(t("notification.export-failed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppButton
      variant="outline-secondary"
      className={clsx(isLoading && "opacity-50")}
      onClick={handleExport}
      disabled={isLoading}
    >
      {isLoading ? (
        <div className="flex h-6 w-6 items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <Download />
      )}
      <span>{t("action.export.excel")}</span>
    </AppButton>
  );
}
